/**
 * @file meta-pixel.ts
 * @description Meta Pixel 이벤트 추적 유틸리티 (SSOT)
 *
 * 모든 Meta Pixel 이벤트 추적은 이 파일에서만 관리합니다.
 * 다른 컴포넌트는 이 파일의 함수만 import해서 사용합니다.
 *
 * 주요 기능:
 * 1. Meta Pixel 이벤트 추적 공통 함수
 * 2. 표준 이벤트 타입 제공 (Lead, ViewContent, InitiateCheckout 등)
 * 3. 광고 최적화에 특화된 파라미터 구조
 * 4. 유입 경로 정보 자동 추가 (traffic-source.ts 사용)
 *
 * 사용 방법:
 * ```typescript
 * import { trackMetaPixel, trackLead, trackViewContent } from '@/lib/utils/meta-pixel';
 *
 * // 일반 이벤트 추적
 * trackMetaPixel('Lead', {
 *   content_name: 'Beta Application',
 *   value: 0,
 *   currency: 'KRW'
 * });
 *
 * // 편의 함수 사용
 * trackLead({
 *   content_name: 'Beta Application Completed',
 *   experience_level: 'beginner'
 * });
 * ```
 *
 * @dependencies
 * - window.fbq (Meta Pixel 스크립트 로드 필요)
 * - @/lib/utils/traffic-source (유입 경로 정보)
 */

import { getTrafficSource, type TrafficSource } from "@/lib/utils/traffic-source";

/**
 * Meta Pixel ID
 * 환경변수로 관리하려면: process.env.NEXT_PUBLIC_META_PIXEL_ID || '1911052246289026'
 */
export const META_PIXEL_ID = "1911052246289026";

// TrafficSource 타입 re-export (하위 호환성)
export type { TrafficSource };

/**
 * Meta Pixel 이벤트 추적 함수 (기본)
 *
 * @param eventName 이벤트 이름 (예: 'Lead', 'ViewContent', 'InitiateCheckout')
 * @param eventParams 이벤트 파라미터 (선택)
 * @param options 추적 옵션
 *
 * @example
 * ```typescript
 * trackMetaPixel('Lead', {
 *   content_name: 'Beta Application',
 *   value: 0,
 *   currency: 'KRW'
 * });
 * ```
 */
export function trackMetaPixel(
  eventName: string,
  eventParams?: Record<string, unknown>,
  options?: {
    includeTrafficSource?: boolean;
  },
): void {
  if (typeof window === "undefined") {
    return;
  }

  const { includeTrafficSource = true } = options || {};

  // 유입 경로 정보 자동 추가
  let enrichedParams = { ...eventParams };
  if (includeTrafficSource) {
    const trafficSource = getTrafficSource();
    enrichedParams = {
      ...enrichedParams,
      traffic_source: trafficSource.source,
      traffic_medium: trafficSource.medium,
      traffic_campaign: trafficSource.campaign,
    };
  }

  console.log(`[Meta Pixel] ${eventName}`, enrichedParams);

  // fbq 함수 사용
  if (typeof window.fbq === "function") {
    try {
      window.fbq("track", eventName, enrichedParams);
      return;
    } catch (error) {
      console.error("[Meta Pixel] fbq 호출 실패:", error);
    }
  }

  console.warn(
    "[Meta Pixel] Meta Pixel이 로드되지 않았습니다. 이벤트가 추적되지 않습니다.",
  );
}

/**
 * Lead 이벤트 추적 (전환 완료)
 * 
 * 베타 신청 완료, 회원가입 완료 등 전환 이벤트에 사용
 * 
 * @param params 이벤트 파라미터
 * 
 * @example
 * ```typescript
 * trackLead({
 *   content_name: 'Beta Application Completed',
 *   value: 0,
 *   currency: 'KRW',
 *   experience_level: 'beginner'
 * });
 * ```
 */
export function trackLead(params?: Record<string, unknown>): void {
  trackMetaPixel("Lead", params);
}

/**
 * ViewContent 이벤트 추적 (콘텐츠 조회)
 * 
 * 페이지 조회, 상품 조회 등에 사용
 * 
 * @param params 이벤트 파라미터
 * 
 * @example
 * ```typescript
 * trackViewContent({
 *   content_name: 'Beta Application Page',
 *   content_category: 'Beta Program',
 *   content_ids: ['beta-apply']
 * });
 * ```
 */
export function trackViewContent(params?: Record<string, unknown>): void {
  trackMetaPixel("ViewContent", params);
}

/**
 * InitiateCheckout 이벤트 추적 (체크아웃 시작)
 * 
 * 폼 제출 시작, 결제 시작 등에 사용
 * 
 * @param params 이벤트 파라미터
 * 
 * @example
 * ```typescript
 * trackInitiateCheckout({
 *   content_name: 'Beta Application Form',
 *   content_category: 'Beta Program',
 *   value: 0,
 *   currency: 'KRW'
 * });
 * ```
 */
export function trackInitiateCheckout(
  params?: Record<string, unknown>,
): void {
  trackMetaPixel("InitiateCheckout", params);
}

/**
 * Purchase 이벤트 추적 (구매 완료)
 * 
 * 결제 완료, 구매 완료 등에 사용
 * 
 * @param params 이벤트 파라미터
 * 
 * @example
 * ```typescript
 * trackPurchase({
 *   content_name: 'Premium Subscription',
 *   value: 9900,
 *   currency: 'KRW',
 *   content_ids: ['premium-monthly']
 * });
 * ```
 */
export function trackPurchase(params?: Record<string, unknown>): void {
  trackMetaPixel("Purchase", params);
}

/**
 * AddToCart 이벤트 추적 (장바구니 추가)
 * 
 * @param params 이벤트 파라미터
 */
export function trackAddToCart(params?: Record<string, unknown>): void {
  trackMetaPixel("AddToCart", params);
}

/**
 * Search 이벤트 추적 (검색)
 * 
 * @param params 이벤트 파라미터
 * 
 * @example
 * ```typescript
 * trackSearch({
 *   search_string: '경매 시뮬레이션'
 * });
 * ```
 */
export function trackSearch(params?: Record<string, unknown>): void {
  trackMetaPixel("Search", params);
}

// TypeScript 타입 확장
declare global {
  interface Window {
    fbq?: (
      command: "track" | "init",
      eventName: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

